import React, { useState } from 'react';
import axios from '../../config/axios';
import { useApi } from '../lib/api';
import Layout, { TopBar } from '../components/Layout';
import { Meta, Display, Chip, Button } from '../components/Atoms';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { confirmDialog } from '../lib/confirm';
import { notifyError, notifySuccess } from '../lib/notify';

/**
 * F2 — Magazyn nasion (inwentarz). Szkielet Qwen, naprawiony w review:
 * data.data (endpoint zwraca {data}), quantity=TEXT (nie Number), puste year/source→null,
 * token --t-text-dim (nie -secondary), Card→div.
 */

const styles = {
  container: { maxWidth: 680, margin: '0 auto', padding: '0 24px 60px' },
  header: { marginBottom: 32 },
  formBox: { border: '1px solid var(--t-line)', borderRadius: 12, padding: 20, marginBottom: 24, background: 'var(--t-bg-elev)', display: 'flex', flexDirection: 'column', gap: 16 },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 200 },
  label: { fontFamily: 'var(--t-mono)', fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--t-text-mute)' },
  input: { background: 'var(--t-bg)', border: '1px solid var(--t-line)', borderRadius: 8, padding: '10px 12px', color: 'var(--t-text)', fontSize: 14, width: '100%', boxSizing: 'border-box', outline: 'none' },
  row: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  card: { border: '1px solid var(--t-line)', borderRadius: 12, padding: 20, marginBottom: 16, background: 'var(--t-bg-elev)', display: 'flex', flexDirection: 'column', gap: 12 },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' },
  actions: { display: 'flex', gap: 8, marginTop: 4 },
};

const EMPTY = { plant_name: '', variety: '', year_acquired: '', quantity: '', source: '', notes: '' };

export default function SeedInventory() {
  const { data, loading, error, refetch } = useApi('/api/seeds');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const seeds = Array.isArray(data?.data) ? data.data : [];
  const shell = (children) => (
    <Layout topBar={<TopBar left={<Meta>Magazyn nasion</Meta>} />}>{children}</Layout>
  );

  const onChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const reset = () => { setForm(EMPTY); setEditingId(null); };

  const startEdit = (s) => {
    setForm({
      plant_name: s.plant_name || '', variety: s.variety || '',
      year_acquired: s.year_acquired || '', quantity: s.quantity || '',
      source: s.source || '', notes: s.notes || '',
    });
    setEditingId(s.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.plant_name.trim()) { notifyError('Podaj nazwę rośliny'); return; }
    const payload = {
      plant_name: form.plant_name.trim(),
      variety: form.variety || null,
      year_acquired: form.year_acquired ? Number(form.year_acquired) : null,
      quantity: form.quantity || null,      // TEXT — „1 saszetka", „50 nasion"
      source: form.source || null,          // '' → null (inaczej backend 400)
      notes: form.notes || null,
    };
    try {
      if (editingId) { await axios.put(`/api/seeds/${editingId}`, payload); notifySuccess('Zapisano zmiany'); }
      else { await axios.post('/api/seeds', payload); notifySuccess('Dodano nasiona'); }
      refetch(); reset();
    } catch (err) {
      notifyError(err.response?.data?.error || 'Nie udało się zapisać');
    }
  };

  const remove = async (id) => {
    const ok = await confirmDialog({ title: 'Usuń nasiona', message: 'Usunąć ten wpis z magazynu?', confirmText: 'Usuń' });
    if (!ok) return;
    try {
      await axios.delete(`/api/seeds/${id}`);
      notifySuccess('Usunięto');
      refetch();
      if (editingId === id) reset();
    } catch { notifyError('Nie udało się usunąć'); }
  };

  const sourceChip = (src) => {
    if (src === 'sklep') return <Chip tone="dim">Sklep</Chip>;
    if (src === 'wymiana') return <Chip tone="sage">Wymiana</Chip>;
    if (src === 'własne') return <Chip tone="amber">Własne</Chip>;
    return null;
  };

  if (loading) return shell(<LoadingState />);
  if (error) return shell(<ErrorState />);

  return shell(
    <div style={styles.container}>
      <div style={styles.header}>
        <Meta color="sage">Zapasy</Meta>
        <Display size={30} style={{ display: 'block', marginTop: 6 }}>Magazyn nasion</Display>
      </div>

      <form style={styles.formBox} onSubmit={submit}>
        <div style={styles.row}>
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="si-plant_name">Nazwa rośliny</label>
            <input id="si-plant_name" style={styles.input} name="plant_name" value={form.plant_name} onChange={onChange} placeholder="np. Pomidor" />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="si-variety">Odmiana</label>
            <input id="si-variety" style={styles.input} name="variety" value={form.variety} onChange={onChange} placeholder="np. Malinowy Ożarowski" />
          </div>
        </div>
        <div style={styles.row}>
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="si-year_acquired">Rok pozyskania</label>
            <input id="si-year_acquired" style={styles.input} type="number" name="year_acquired" value={form.year_acquired} onChange={onChange} placeholder="2024" />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="si-quantity">Ilość</label>
            <input id="si-quantity" style={styles.input} name="quantity" value={form.quantity} onChange={onChange} placeholder="np. 1 saszetka" />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="si-source">Źródło</label>
            <select id="si-source" style={styles.input} name="source" value={form.source} onChange={onChange}>
              <option value="">— wybierz —</option>
              <option value="sklep">Sklep</option>
              <option value="wymiana">Wymiana</option>
              <option value="własne">Własne</option>
            </select>
          </div>
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor="si-notes">Notatki</label>
          <input id="si-notes" style={styles.input} name="notes" value={form.notes} onChange={onChange} placeholder="np. z targu nasion, dobra kiełkowalność" />
        </div>
        <div style={styles.actions}>
          <Button variant="primary" type="submit">{editingId ? 'Zapisz zmiany' : 'Dodaj nasiona'}</Button>
          {editingId && <Button variant="ghost" type="button" onClick={reset}>Anuluj</Button>}
        </div>
      </form>

      {seeds.length === 0 ? (
        <EmptyState title="Pusty magazyn" description="Dodaj pierwsze nasiona — przydadzą się przy planowaniu sezonu." />
      ) : seeds.map((s) => (
        <div key={s.id} style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 500, color: 'var(--t-text)' }}>{s.plant_name}</div>
              {s.variety && <Meta>{s.variety}</Meta>}
            </div>
            {sourceChip(s.source)}
          </div>
          <div style={styles.chipRow}>
            {s.quantity && <Meta>Ilość: {s.quantity}</Meta>}
            {s.year_acquired && <Meta>Rok: {s.year_acquired}</Meta>}
            {s.agingWarning && <Chip tone="amber">Może tracić siłę kiełkowania ({s.yearsOld} lata)</Chip>}
          </div>
          {s.notes && <div style={{ fontSize: 14, color: 'var(--t-text-dim)', lineHeight: 1.5 }}>{s.notes}</div>}
          <div style={styles.actions}>
            <Button variant="ghost" onClick={() => startEdit(s)}>Edytuj</Button>
            <Button variant="ghost" onClick={() => remove(s.id)}>Usuń</Button>
          </div>
        </div>
      ))}
    </div>
  );
}
